import { useTranslation } from 'react-i18next';
import AnalyticsTable from '@customer/components/tables/analytics-table/AnalyticsTable';
import AnalyticsSubPage from '@customer/templates/health-sub-page/AnalyticsSubPage';
import { useCallback, useMemo, useState } from 'react';
import {
  AssetBasicModel,
  AssetListScenarios,
  PerformanceVIAssetsFilters,
} from '@customer/store/apis/assetsApi';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { AssetInteractionsAccordion, AssetSidePanelTab } from '@customer/store/slices/globalSlice';
import usePerformanceSubPage from '@customer/templates/performance-sub-page/usePerformanceSubPage';
import {
  columnDefAssetGroup,
  columnDefAssetName,
  columnDefDeviceType,
  columnDefSoftwareStatus,
  columnDefVehicleInteractionsAlert,
  columnDefVehicleInteractionsAlarm,
  columnDefVehicleInteractionsTotal,
  columnDefVehicleInteractionsAware,
  columnDefObjectType,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef } from 'ag-grid-community';
import ChartScenarios from '@customer/components/charts/chart-scenarios/ChartScenarios';
import { ChartColorScheme, ScenarioConfig } from '@customer/components/charts/config';
import { PerformanceVIScenarioQueryParams, StatsScenario } from '@customer/store/apis/scenariosApi';
import BarChartByVehicleInteractionType from '@customer/components/charts/by-vehicle-interaction/BarChartByVehicleInteractionType';
import HistogramChartScenario from '@customer/components/charts/histogram-chart-scenario/HistogramChartScenario';
import AreaChartScenario from '@customer/components/charts/area-chart-scenario/AreaChartScenario';
import { FilterGroup } from '@customer/filters/types';
import VehicleInteractionsOverview from './components/overview/VehicleInteractionsOverview';
import LocationFilter from '@customer/components/maps/location-filter/LocationFilter';
import { useGetVIHeatmapQuery } from '@customer/store/apis/heatmapsApi';
import useLocationFilterController from '@customer/components/maps/location-filter/useLocationFilterController';
import BarChartByObjectType from '@customer/components/charts/by-object-type/BarChartByObjectType';
import PerformanceTimeRange from '@customer/components/inputs/performance-time-range/PerformanceTimeRange';
import BarChartByAssetGroup from '@customer/components/charts/by-asset-group/BarChartByAssetGroup';

const tableSliceKey = 'AssetsPerformanceVehicleInteractions';
const API_TABLE_LIMIT = 20;
const defaultColDef: ColDef<AssetBasicModel> = { sortable: false };

export function VehicleInteractions() {
  const { t } = useTranslation();

  const [columnDefs] = useState<ColDef<AssetBasicModel>[]>([
    { ...columnDefAssetName(t), maxWidth: 150 },
    { ...columnDefObjectType(t), maxWidth: 150 },
    { ...columnDefDeviceType(t), maxWidth: 110 },
    { ...columnDefAssetGroup(t) },
    { ...columnDefVehicleInteractionsAlarm(t), maxWidth: 110 },
    { ...columnDefVehicleInteractionsAlert(t), maxWidth: 110 },
    { ...columnDefVehicleInteractionsAware(t), maxWidth: 110 },
    { ...columnDefVehicleInteractionsTotal(t), maxWidth: 110 },
    { ...columnDefSoftwareStatus(t), maxWidth: 150 },
  ]);

  const { assets, hasData, noDataDescription, countAll, countFilter, onRowClick } =
    usePerformanceSubPage<PerformanceVIAssetsFilters>({
      scenario: AssetListScenarios.PerformanceVehicleInteractions,
      filterGroup: FilterGroup.PerformanceVI,
      sidePanelTab: AssetSidePanelTab.Interactions,
      sidePanelDefaultAccordion: AssetInteractionsAccordion.VehicleInteractions,
    });

  const getDataSetLabel = useCallback(
    (label: string) => {
      return t(`vi.level.${label}`);
    },
    [t],
  );

  const scenarioConfigs = useMemo<ScenarioConfig[]>(
    () => [
      {
        title: t('ByTimeRange'),
        ChartComponent: AreaChartScenario<PerformanceVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByTimeRange,
        hideResultsLimited: true,
        enableCustomTimeRange: true,
      },
      {
        title: t('ByTimeOfDay'),
        ChartComponent: HistogramChartScenario<PerformanceVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByTimeOfDay,
        hideResultsLimited: true,
      },
      {
        title: t('ByLOAssetGroup'),
        ChartComponent: BarChartByAssetGroup<PerformanceVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByLocalObjectAssetGroup,
      },
      {
        title: t('ByLOObjectType'),
        ChartComponent: BarChartByObjectType<PerformanceVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByObjectType,
      },
      {
        title: t('ByInteractionType'),
        ChartComponent: BarChartByVehicleInteractionType<PerformanceVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByInteractionType,
        hideResultsLimited: true,
      },
    ],
    [t],
  );

  const locationFilterProps = useLocationFilterController({
    filterGroup: FilterGroup.PerformanceVI,
    useHeatmapQuery: useGetVIHeatmapQuery,
  });

  return (
    <AnalyticsSubPage
      countAll={countAll}
      countFilter={countFilter}
      filters={FilterGroup.PerformanceVI}
      header={<PerformanceTimeRange disabled={false} />}
      content={
        <>
          <VehicleInteractionsOverview />

          <ChartScenarios
            title={t('VehicleInteractions')}
            scenarioConfigs={scenarioConfigs}
            filterGroup={FilterGroup.PerformanceVI}
            colorScheme={ChartColorScheme.RedOrangeYellow}
            getDataSetLabel={getDataSetLabel}
            tooltipTransKey="InteractionsWithCount"
            includeTimeRange
          />

          <LocationFilter {...locationFilterProps} />

          <AnalyticsTable
            title={t('Assets')}
            hasData={hasData}
            noDataDescription={noDataDescription}
            apiResultLimit={API_TABLE_LIMIT}
          >
            <GridControlled<AssetBasicModel>
              sliceKey={tableSliceKey}
              rowData={assets}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              noRowsMessage={t('No assets')}
              onRowClick={onRowClick}
            />
          </AnalyticsTable>
        </>
      }
    />
  );
}

export default VehicleInteractions;
